import SEO from 'global/SEO';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState, useMemo } from 'react';
import Link from 'next/link';
import If from 'global/ErrorHandlers/If';
import { convertToSlug } from 'utils/general';
import { effects, selectors } from 'features/catalog/slice';
import { useRouter } from 'next/router';
import { selectors as credentialsSelectors } from 'lib/entities/credentials';
import useBondsAnalytics from 'global/TradingModal/RealTradingModal/ModalBonds/useBondsAnalytics';
import TableSector from './TableSector';
import HeaderSector from './HeaderSector';
import { BoxSectors, BoxTable } from './styled';

interface SectorProps {
  slugs?: Array<string>;
}

const LIST_NOT_SECTORS = ['Listing Board'];
const FR_BONDS_SECTOR_ALIAS = 'fr-bonds';

function Sector({ slugs = [] }: SectorProps) {
  const router = useRouter();
  const dispatch = useDispatch();
  const { FRBondsNavigateTrackers } = useBondsAnalytics();

  const { list: catalog, sectors, subsectors } = useSelector(selectors);
  const { isLoggedIn } = useSelector(credentialsSelectors);
  const [sector, subsector] = slugs;

  const [symbolList, setSymbolList] = useState([]);
  const [visible, setVisible] = useState<boolean>(false);

  const getCatalogCompanies = async () => {
    await dispatch(effects.getCompanyList({ sector, subsector }));
  };

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(effects.getSectorList());
    }
  }, []);

  useEffect(() => {
    if (sector) {
      if (isLoggedIn) {
        getCatalogCompanies();
      }
      setVisible(false);
    }

    if (!sector) {
      setVisible(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sector, subsector]);

  const mergeCompanyList = useMemo(() => {
    const sectorList = catalog?.[sector];
    if (sectorList) {
      const companyList = [];
      Object.values(sectorList).forEach((item: Array<any>) => {
        if (item) {
          companyList.push(...item);
        }
      });
      return companyList;
    }
    return [];
  }, [catalog]);

  // Set Company & Symbol List
  useEffect(() => {
    if (sector && subsector && catalog?.[sector]?.[subsector]) {
      const listSymbol = catalog?.[sector]?.[subsector].map(
        (company) => company.symbol,
      );
      setSymbolList(listSymbol);
    }

    if (sector && !subsector && catalog?.[sector]) {
      if (!sector.includes('indeks')) {
        const listSymbol = mergeCompanyList.map((company) => company.symbol);
        setSymbolList(listSymbol);
      }
    }

    return () => {
      setSymbolList([]);
    };
  }, [catalog, sector, subsector]);

  const toggleSectorList = () => {
    setVisible(!visible);
  };

  let sectorTitle = 'All Company Sector';
  const activeSector = sectors.find(
    (item) => convertToSlug(item.alias1) === convertToSlug(sector),
  );
  const activeSubSector = subsectors.find(
    (item) => convertToSlug(item.alias1) === convertToSlug(subsector),
  );

  const showSector = sector && activeSector;
  const showSubSector = subsector && activeSubSector;

  if (showSector) {
    sectorTitle = `Sektor ${activeSector?.name}`;
  }

  if (showSubSector) {
    sectorTitle = `Sektor ${activeSubSector?.name}`;
  }

  const handleBackButton = () => {
    if (!sector && !subsector) {
      return;
    }

    if (showSubSector) {
      router.push(`/sector/${sector}`);
      return;
    }

    if (showSector) {
      router.push('/sector');
    }
  };

  const isFRSector = convertToSlug(sector) === FR_BONDS_SECTOR_ALIAS;

  useEffect(() => {
    if (isFRSector) {
      FRBondsNavigateTrackers.onBondPageViewed('Sector Page');
    }
  }, [isFRSector]);

  return (
    <>
      <SEO title={sectorTitle} />
      <HeaderSector
        sector={activeSector}
        subsector={activeSubSector}
        showSector={showSector}
        showSubSector={showSubSector}
        onBack={handleBackButton}
        toggleSectorList={toggleSectorList}
      />
      <If condition={visible}>
        <BoxSectors>
          {sectors.map((item) => {
            const isNotSector = LIST_NOT_SECTORS.includes(item.name);
            return (
              <If condition={!isNotSector} key={item.alias1}>
                <Link href={`/sector/${item.alias1}`}>
                  <a>{item.name}</a>
                </Link>
              </If>
            );
          })}
        </BoxSectors>
      </If>
      <If condition={activeSector || activeSubSector}>
        <BoxTable>
          <TableSector
            sector={sector}
            subsector={subsector}
            symbolList={symbolList}
            isFRSector={isFRSector}
          />
        </BoxTable>
      </If>
    </>
  );
}

export default Sector;
